import React from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAuth } from '../../auth/AuthProvider';
import useLandingData from '../../hooks/useLandingData';
import LandingTopBar from '../../components/landing/LandingTopBar';
import HeroAndRankingSection from '../../components/landing/HeroAndRankingSection';
import NewUsersSection from '../../components/landing/NewUsersSection';
import LoadingState from '../../components/landing/LoadingState';
import EmptyState from '../../components/landing/EmptyState';
import SectionHeader from '../../components/landing/SectionHeader';
import ProjectCard from '../project-dashboard/components/ProjectCard';
import ChallengeCard from '../project-detail/components/ChallengeCard';
import RecentProofCard from '../user-profile-dashboard/components/RecentProofCard';
import NoticeSection from '../project-dashboard/components/NoticeSection';
import LabsSection from '../labs/components/LabsSection';
const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    isLoading,
    projectGrid,
    challengeGrid,
    proofGrid,
    feedGrid,
    recentNotices,
    recentLabs,
    newUsers,
    monthlyRankings,
  } = useLandingData();

  const monthLabel = `${new Date().getUTCFullYear()}년 ${new Date().getUTCMonth() + 1}월`;
  return (
    <div className="min-h-screen bg-background">
      <Seo 
        title="꿈 만들기, 목표 계획하기, 꿈현 - 꿈을 현실로 만드는 사람들의 도전 플랫폼" 
        description="꿈현.Labs: 꿈 만들기, 목표 계획하기, 꿈을 현실로 만드는 사람들을 위한 도전 플랫폼. 꿈, 목표, 도전을 체계적으로 관리하고 현실로 만들어보세요."
        canonicalPath="/" 
      />

      <LandingTopBar
        onLogoClick={() => navigate('/')}
        onPrimaryActionClick={() => navigate(user?.id ? '/project-dashboard' : '/login-registration')}
        primaryActionLabel={user?.id ? '내 프로필' : '로그인'}
      />

      <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-10 md:py-12">
        <HeroAndRankingSection
          monthLabel={monthLabel}
          monthlyRankings={monthlyRankings}
          onStart={() => navigate('/login-registration')}
          onOpenRankings={() => navigate('/rankings')}
          onOpenRankingUser={(userId) => navigate(`/people/user?id=${userId}`)}
        />

        {isLoading ? (
          <div className="mb-10 md:mb-12">
            <LoadingState />
          </div>
        ) : (
          <NewUsersSection
            users={newUsers}
            onOpenAll={() => navigate('/people')}
            onOpenUser={(userId) => navigate(`/people/user?id=${userId}`)}
          />
        )}

        <section className="mb-10 md:mb-12">
          <div className="mb-6 md:mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <SectionHeader title="공지사항" actionLabel="전체 보기" onAction={() => navigate('/notices')} />
                <NoticeSection notices={recentNotices} hideHeader />
              </div>

              <div>
                <SectionHeader title="꿈현 Labs에게 요구합니다!" actionLabel="전체 보기" onAction={() => navigate('/labs')} />
                <LabsSection items={recentLabs} hideHeader />
              </div>
            </div>
          </div>

          <SectionHeader title="피드" actionLabel="전체 보기" onAction={() => navigate('/feed?view=list&page=1')} />

          {isLoading ? (
            <LoadingState />
          ) : feedGrid.length === 0 ? (
            <EmptyState message="공개 피드가 아직 없습니다." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-10 md:mb-12">
              {feedGrid.map((feed) => (
                <button
                  key={feed.id}
                  type="button"
                  onClick={() => navigate(`/feed-detail?id=${feed.id}`)}
                  className="text-left bg-card rounded-lg border border-border overflow-hidden hover:shadow-gaming-md transition-gaming"
                >
                  <div className="aspect-[4/3] bg-muted border-b border-border">
                    {feed.thumbnailUrl ? (
                      <img src={feed.thumbnailUrl} alt={feed.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground caption">
                        이미지 없음
                      </div>
                    )}
                  </div>
                  <div className="p-3 md:p-4">
                    <div className="text-sm md:text-base font-heading font-semibold text-foreground line-clamp-2">{feed.title}</div>
                    <div className="text-[11px] text-muted-foreground caption mt-1">
                      {feed.createdAt} · {feed.authorName}
                    </div>
                    <div className="text-[11px] text-muted-foreground caption mt-2">
                      좋아요 {Number(feed.likeCount || 0)} · 댓글 {Number(feed.commentCount || 0)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <SectionHeader title="꿈&목표" />

          {isLoading ? (
            <LoadingState />
          ) : projectGrid.length === 0 ? (
            <EmptyState message="공유된 목표가 아직 없습니다." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {projectGrid.map((p) => (
                <ProjectCard
                  key={p.id}
                  project={p}
                />
              ))}
            </div>
          )}
        </section>

        <section className="mb-10 md:mb-12">
          <SectionHeader title="최신 챌린지" />

          {isLoading ? (
            <LoadingState />
          ) : challengeGrid.length === 0 ? (
            <EmptyState message="공개 챌린지가 아직 없습니다." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {challengeGrid.map((c) => (
                <ChallengeCard
                  key={c.id}
                  challenge={c}
                  variant="tall"
                  onClick={() => navigate(`/challenge-detail?id=${c.id}`)}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionHeader title="최신 인증" />

          {isLoading ? (
            <LoadingState />
          ) : proofGrid.length === 0 ? (
            <EmptyState message="공개 인증이 아직 없습니다." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {proofGrid.map((proof) => (
                <RecentProofCard
                  key={proof?.id}
                  proof={proof}
                  onOpenDetail={() => {
                    if (!proof?.challengeId) return;
                    navigate(`/challenge-detail?id=${proof.challengeId}`);
                  }}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Landing;
